import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Stack, UnstyledButton } from "@mantine/core";

import { Text, Title } from "components";
import { AppConstants, RouteConstants, ServiceConstants } from "constant";
import { AppUtils, StorageUtils } from "utils";
import { ChatAttachmentResponse, ChatMessageResponse, ChatResponse, UserResponse } from "types"
import { useListHook } from "hooks";
import { useSelector } from "react-redux";
import { IRootState } from "config/redux";
import moment from "moment";
import ChatInput from "./input";
import { IconFile } from "@tabler/icons-react";
import { ChatService } from "services";
import { useNavigate } from "react-router-dom";

const { STORAGE_USER } = AppConstants;
const { REQUEST_CHATS_MESSAGES_LIST_ID, REQUEST_CHATS_MESSAGES_CREATE_ID, REQUEST_CHATS_MESSAGES_ATTACHMENT_UPLOAD_ID } = ServiceConstants;

interface ChatMessagesProps {
  chat?: ChatResponse;
  setActiveChat: (val: ChatResponse) => void
}

const ChatMessages = ({ chat, setActiveChat }: ChatMessagesProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_MESSAGES_LIST_ID]);
  const createServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_MESSAGES_CREATE_ID]);
  const uploadServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_MESSAGES_ATTACHMENT_UPLOAD_ID]);
  const { page, content, updatePageable, totalPages, setPage, resetList } = useListHook();
  const [loggedUser] = useState<UserResponse>(StorageUtils.getObject(STORAGE_USER));
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const prevHeight = useRef<number>(0);
  const prevChat = useRef<number | undefined>(undefined);

  useEffect(() => {
    AppUtils.resolveServiceObject(listServiceObject, REQUEST_CHATS_MESSAGES_LIST_ID, updatePageable);
    AppUtils.resolveServiceObject(createServiceObject, REQUEST_CHATS_MESSAGES_CREATE_ID, (val) => {
      setActiveChat({ ...chat!, lastMessage: val[val.length - 1] });
      setMessages(old => ([...val, ...old]));
      setTimeout(() => {
        const objDiv = document.getElementById(`messages`);
        if (objDiv) objDiv.scrollTop = objDiv.scrollHeight;
      }, 0);
    });
    AppUtils.resolveServiceObject(uploadServiceObject, REQUEST_CHATS_MESSAGES_ATTACHMENT_UPLOAD_ID, (val) => {
      setMessages(old => ([...val, ...old]));
    });
  }, [listServiceObject, createServiceObject, uploadServiceObject]);

  useEffect(() => {
    if (chat) {
      if (chat.id !== prevChat.current) {
        prevHeight.current = 0;
        setMessages([]);
        resetList();
        if (chat.id) {
          ChatService.chatMessages(chat.id, { page: 0 });
        }
      }
    }
    prevChat.current = chat?.id;
  }, [chat]);

  useEffect(() => {
    if (page === undefined || !chat || !chat.id) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    if (page === 0) {
      ChatService.chatMessagesRead(chat!.id);
    }
    if (page > 0 && page < totalPages) {
      ChatService.chatMessages(chat!.id, { page: page });
    }
  }, [page]);

  useEffect(() => {
    if (content) {
      setMessages(old => ([...content, ...old]));
      scrollToBottom();
      activateScrollListener();
    }
  }, [content]);

  useEffect(() => {
    if (page === 0) {
      scrollToBottom();
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    const objDiv = document.getElementById(`messages`);
    if (objDiv) {
      objDiv.scrollTop = prevHeight.current === 0 ? objDiv.scrollHeight : (objDiv.scrollHeight - prevHeight.current);
      prevHeight.current = objDiv.scrollHeight;
    }
  }, [page]);

  const activateScrollListener = useCallback(() => {
    const objDiv = document.getElementById(`messages`);
    if (!objDiv) {
      return null;
    }
    if (page && page === totalPages - 1) {
      return null;
    }
    return objDiv.addEventListener('scroll', () => {
      if (objDiv.scrollTop < 20) {
        deactivateScrollListener();
        setPage((page ?? 0) + 2);
      }
    });
  }, [page, totalPages]);

  const deactivateScrollListener = useCallback(() => {
    document.getElementById(`messages`)?.removeEventListener('scroll', activateScrollListener);
  }, []);

  const preview = useCallback((item: ChatAttachmentResponse) => window.open(`${process.env.REACT_APP_API_URL_ATTACHMENTS}/chat/${chat!.id}/${item.id}.${item.extension}`, '_blank'), [chat]);

  const renderMessages = useMemo(() => {
    if (!messages || messages.length === 0) {
      return <></>
    }
    let renderedContent: React.ReactElement[] = [];
    let dates: string[] = [];
    const reversed = messages.sort((a, b) => new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime() ? -1 : 1);
    reversed.forEach((el, index) => {
      const date = moment(new Date(el.createdAt)).startOf('day').toDate();
      if (!dates.includes(date.toISOString())) {
        dates.push(date.toISOString());
        renderedContent.push(
          <Flex justify={'center'} align={'center'} key={`message-${page}-${index}`} my={'xs'}>
            <Text
              key={`date-${index}`}
              ta={'center'}
              c={'var(--mantine-color-gray-6)'}
              px={'sm'}
            >
              {t('format.date', { date: date })}
            </Text>
          </Flex>
        )
      }
      renderedContent.push(
        <Flex justify={'space-between'} key={`message-${index}`} my={'xs'} id={`message-${index}`}>
          {el.createdBy.type === 'EMPLOYEE' && <Box></Box>}
          <Box>
            <Box
              style={{ borderRadius: 8, flexGrow: 1, flexShrink: 1 }}
              bg={el.createdBy.type === 'EMPLOYEE' ? 'var(--mantine-color-gray-6)' : 'var(--mantine-color-primary-6)'}
              c={'var(--mantine-color-white'}
              p={'sm'}
            >
              {loggedUser.profile.id === 1 && el.createdBy.type === 'EMPLOYEE' && <Text size={'xs'}>{el.createdBy.fullName}</Text>}
              {el.attachment ?
                <UnstyledButton onClick={() => preview(el.attachment)}>
                  <Flex align={'center'}>
                    <IconFile size={32} />
                    <Text ml={'xs'} maw={120}>{el.attachment.name}</Text>
                  </Flex>
                </UnstyledButton>
                : <Text>{el.message}</Text>
              }
              <Text ta={'right'} size={'xs'}>{t('format.time', { date: new Date(el.createdAt)})}</Text>
            </Box>
          </Box>
          {el.createdBy.type === 'CLIENT' && <Box></Box>}
        </Flex>
      )
    });
    return renderedContent;
  }, [chat, messages, loggedUser]);

  return (
    <Box h={'calc(100vh - 120px)'}>
      {chat?.user &&
        <Box bg={'var(--mantine-color-gray-1)'} p={'md'} style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
          <Title
            order={6}
            mb={0}
            mt={0}
            onClick={() => navigate(`${RouteConstants.ROUTE_CLIENTS}/${chat.user.id}` )}
            style={{ cursor: 'pointer' }}
          >
            {chat.user.fullName}
          </Title>
        </Box>
      }
      <Stack align={'stretch'} justify={'flex-end'} gap={0} h={'100%'}>
        <Box px={'lg'} style={{ overflowY: 'auto' }} id={'messages'}>
          {renderMessages}
        </Box>
        {/* loggedUser.id = 1 - Admin */}
        {chat && loggedUser.profile.id !== 1 && <ChatInput chat={chat} loggedUser={loggedUser} t={t} />}
      </Stack>
    </Box>
  )
}

export default ChatMessages;